import { useId, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, Settings } from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface SettingsNavigationProps {
  items: { label: string; to: string; icon: ReactNode }[];
  currentPath: string;
  collapsed?: boolean;
  mobile?: boolean;
  onNavigate?: () => void;
}

export function SettingsNavigation({
  items, currentPath, collapsed = false, mobile = false, onNavigate,
}: SettingsNavigationProps) {
  const isActivePath = (path: string) => currentPath === path || currentPath.startsWith(`${path}/`);
  const isActive = items.some((item) => isActivePath(item.to));
  const [expansion, setExpansion] = useState({ path: currentPath, active: isActive, open: isActive });
  const contentId = useId();

  // Track the route in state so a suspended navigation cannot consume an
  // auto-expand update before the destination page actually commits.
  if (expansion.path !== currentPath || expansion.active !== isActive) {
    setExpansion({ path: currentPath, active: isActive, open: isActive || expansion.open });
  }
  const open = expansion.open;

  if (!items.length) return null;

  const triggerClass = cn(
    'flex w-full items-center rounded-lg text-[12px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
    collapsed ? 'h-9 justify-center' : 'gap-2 px-2.5 py-1.5',
    mobile
      ? 'rounded-2xl px-4 py-3 text-[13px] text-slate-700 hover:bg-slate-100'
      : isActive ? 'bg-white/12 text-white' : 'text-blue-100/78 hover:bg-white/6 hover:text-white',
  );
  const icon = <Settings className="h-4 w-4" />;

  if (collapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={triggerClass} aria-label="Settings" title="Settings">
            {icon}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" className="w-56">
          {items.map((item) => (
            <DropdownMenuItem key={item.to} asChild>
              <Link to={item.to} onClick={onNavigate} aria-current={isActivePath(item.to) ? 'page' : undefined}
                className={cn('flex items-center gap-2', isActivePath(item.to) && 'bg-slate-100 font-semibold')}>
                {item.icon}{item.label}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div>
      <button type="button" className={triggerClass} onClick={() => setExpansion({ path: currentPath, active: isActive, open: !open })}
        aria-expanded={open} aria-controls={contentId}>
        <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-md', !mobile && 'bg-white/[0.04]')}>
          {icon}
        </span>
        <span>Settings</span>
        <ChevronDown className={cn('ml-auto h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      <div id={contentId} hidden={!open} className={cn('ml-6 mt-1 space-y-0.5 border-l pl-2', mobile ? 'border-slate-200' : 'border-white/15')}>
        {items.map((item) => (
          <Link key={item.to} to={item.to} onClick={onNavigate}
            aria-current={isActivePath(item.to) ? 'page' : undefined}
            className={cn('flex items-center gap-2 rounded-lg px-2 py-2 text-[12px] font-semibold transition-colors',
              mobile
                ? isActivePath(item.to) ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-100'
                : isActivePath(item.to) ? 'bg-white/12 text-white' : 'text-blue-100/78 hover:bg-white/6 hover:text-white')}>
            <span className="shrink-0">{item.icon}</span>
            <span>{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
